import { useEffect, useCallback } from 'react';

export const useKeyboardNavigation = (enabled: boolean = true) => {
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!enabled) return;

    // Add keyboard navigation class to body for enhanced focus styles
    document.body.classList.add('keyboard-nav-active');

    // Handle Escape key to close modals, dropdowns, etc.
    if (event.key === 'Escape') {
      const activeElement = document.activeElement as HTMLElement;
      if (activeElement && activeElement.blur) {
        activeElement.blur();
      }
      
      // Close any open dropdowns or modals
      const dropdowns = document.querySelectorAll('[data-dropdown-open="true"]');
      dropdowns.forEach(dropdown => {
        dropdown.setAttribute('data-dropdown-open', 'false');
      });
    }

    // Handle Tab navigation with visual indicators
    if (event.key === 'Tab') {
      document.body.classList.add('keyboard-nav-active');
    }

    // Handle Arrow key navigation for custom components
    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
      const focusableElements = document.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      
      const currentIndex = Array.from(focusableElements).findIndex(
        element => element === document.activeElement
      );

      if (currentIndex !== -1) {
        let nextIndex = currentIndex;
        
        switch (event.key) {
          case 'ArrowDown':
          case 'ArrowRight':
            nextIndex = (currentIndex + 1) % focusableElements.length;
            break;
          case 'ArrowUp':
          case 'ArrowLeft':
            nextIndex = currentIndex === 0 ? focusableElements.length - 1 : currentIndex - 1;
            break;
        }

        if (nextIndex !== currentIndex) {
          event.preventDefault();
          (focusableElements[nextIndex] as HTMLElement).focus();
        }
      }
    }
  }, [enabled]);

  const handleMouseDown = useCallback(() => {
    // Remove keyboard navigation class when using mouse
    document.body.classList.remove('keyboard-nav-active');
  }, []);

  useEffect(() => {
    if (enabled) {
      document.addEventListener('keydown', handleKeyDown);
      document.addEventListener('mousedown', handleMouseDown);

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.removeEventListener('mousedown', handleMouseDown);
      };
    }
  }, [enabled, handleKeyDown, handleMouseDown]);

  return {
    // Helper function to create focus trap for modals
    createFocusTrap: (containerRef: React.RefObject<HTMLElement>) => {
      if (!containerRef.current) return;

      const focusableElements = containerRef.current.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );

      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

      const handleTrapKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Tab') {
          if (event.shiftKey) {
            if (document.activeElement === firstElement) {
              lastElement.focus();
              event.preventDefault();
            }
          } else {
            if (document.activeElement === lastElement) {
              firstElement.focus();
              event.preventDefault();
            }
          }
        }
      };

      containerRef.current.addEventListener('keydown', handleTrapKeyDown);
      firstElement?.focus();

      return () => {
        containerRef.current?.removeEventListener('keydown', handleTrapKeyDown);
      };
    }
  };
};